// /proyectos/:id — las unidades del proyecto.
//
// El journey del jefe entra por Proyectos (handoff del panel,
// 2026-09-15): aterriza en su cartera, entra a un proyecto y desde ahí
// va a SUS observaciones. El panel postventa v2 (design/claude-design/
// panel-postventa-v2/README.md §2) separa las dos cosas en dos rutas:
// acá quedan los KPIs y la tabla de unidades; las observaciones viven
// en /proyectos/:id/observaciones (ObservacionesDelProyectoRoute), y la
// fila de unidad navega hasta allá con `?unidad=<assetId>`.
//
// La tabla de unidades sigue siendo la ÚNICA superficie desde donde se
// activa una unidad (el handoff la reemplaza por un selector dentro del
// drawer de activación que todavía no existe); borrarla dejaría al jefe
// sin poder invitar a nadie.
import { useEffect, useId, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  entregaLabel,
  canReadOwnerSurveys,
  isFindingManagementRole,
  isInvitationCreateRole,
  type CarteraDashboardAssetItem,
  type RecipeMeResponse,
} from "@bitacorapro/api-types";
import { deliveryAlreadyHappened } from "@bitacorapro/core-engine";
import { ActivateUnitDrawer } from "../components/ActivateUnitDrawer";
import { EntregaDrawer } from "../components/EntregaDrawer";
import { CierreKpis, PropietariosSinContacto } from "../components/CierreSinRespuesta";
import { EncuestaKpi } from "../components/EncuestaKpi";
import { EmptyState } from "../components/EmptyState";
import { Icon } from "../components/icons";
import { KpiTile } from "../components/KpiTile";
import { PageError } from "../components/PageError";
import { PageSkeleton } from "../components/PageSkeleton";
import { Skel } from "../components/Skel";
import { markProjectVisited } from "../lib/activeProject";
import { canUsePortfolioAnalysis } from "../lib/nav";
import { useSession } from "../lib/auth/useSession";
import { ApiError } from "../lib/api/error";
import { carteraDashboardApi } from "../lib/api/endpoints/cartera-dashboard";
import {
  useCarteraProyectoDetail,
  usePostventaClosureSummary,
  usePostventaSurveySummary,
  useRecipe,
} from "../lib/api/queries";
import { useQueryView } from "../lib/loading/useQueryView";
import { pendientesDeGestion } from "../lib/postventa/phase";
import { RefreshNotices, SlowNotice } from "./queryViewKit";

const PAGE_LIMIT = 50;

type ActivacionFiltro = "all" | "1" | "0";
type GestionFiltro = "all" | "yes" | "no";

// "Activada" means exactly one thing: the unit's asset_status is one of
// the statuses the recipe says may receive observations (ADR-0065
// amended). NOT `deliveryActaSignedAt` — that field is deprecated
// precisely because a signed acta stopped being the gate, and NOT the
// literal "en_garantia", which would be a per-slug branch laundered
// through a string. Shared by the row (pill) and the route (filters and
// counts) so the two never disagree about which units are live.
function isUnitActivated(a: CarteraDashboardAssetItem, recipe: RecipeMeResponse | null): boolean {
  const activationStatuses = recipe?.observationsRequireAssetStatusIn ?? [];
  return a.assetStatus != null && activationStatuses.includes(a.assetStatus);
}

// La identidad de la unidad es su número («302»); la dirección es la del
// edificio y sólo toma el lugar cuando la unidad no tiene número.
function unitTitle(a: CarteraDashboardAssetItem): string {
  return a.unit || a.address || "(sin unidad)";
}

/** 4 tiles base, + 2 del cierre, + 1 del NPS (ADR-0153). */
function kpiGridClass(cierre: boolean, nps: boolean): string {
  const n = 4 + (cierre ? 2 : 0) + (nps ? 1 : 0);
  return n === 4 ? "kpi-grid" : `kpi-grid kpi-grid--${n}`;
}

export function DetalleProyecto() {
  const { id } = useParams<{ id: string }>();
  const { data: recipe } = useRecipe();
  const detailQuery = useCarteraProyectoDetail(id, { limit: PAGE_LIMIT });
  const { data } = detailQuery;
  // loading / error / disabled / ready from the contract: a missing `:id`
  // leaves the query disabled, which is «no encontrado», never a
  // skeleton that waits forever.
  const view = useQueryView(detailQuery);
  // ADR-0142 — conteo por motivo de cierre y propietarios sin contacto.
  // Sólo con ciclo de observaciones y para roles de gestión (el servidor
  // responde 403 al resto); `enabled: false` del servidor = no se pinta.
  const sessionRol = useSession()?.organization.role ?? null;
  const { data: cierre } = usePostventaClosureSummary(
    id,
    (recipe?.observationStatuses.length ?? 0) > 0 &&
      sessionRol !== null &&
      isFindingManagementRole(sessionRol)
  );
  // ADR-0153 — el tile NPS: gerente y jefe, y sólo si la receta encuesta.
  const { data: encuestas } = usePostventaSurveySummary(
    id,
    recipe?.ownerSurveys !== undefined && sessionRol !== null && canReadOwnerSurveys(sessionRol)
  );
  const [extraPages, setExtraPages] = useState<CarteraDashboardAssetItem[][]>([]);
  const [manualCursor, setManualCursor] = useState<string | null | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  // Filtros de la tabla (README §State Management): en el cliente, no en
  // la URL — a diferencia de las observaciones, nadie comparte «las
  // unidades sin activar del Mirador» por enlace.
  const [activacion, setActivacion] = useState<ActivacionFiltro>("all");
  const [gestion, setGestion] = useState<GestionFiltro>("all");
  const [q, setQ] = useState("");

  // El único punto de escritura del «proyecto activo» (lib/activeProject):
  // las rutas antiguas /observaciones redirigen al último proyecto que
  // el usuario abrió. Se escribe recién cuando el servidor confirmó que
  // el id existe — un 404 no deja rastro.
  useEffect(() => {
    if (data && id) markProjectVisited(id);
  }, [data, id]);

  const groupLabel = recipe?.labels.assetGroup ?? "Proyecto";
  const groupPlural = recipe?.labels.assetGroupPlural ?? "Proyectos";
  const assetLabel = data?.labels.asset ?? recipe?.labels.asset ?? "Unidad";
  const assetPlural = data?.labels.assetPlural ?? recipe?.labels.assetPlural ?? "Unidades";

  const items = useMemo(() => [...(data?.items ?? []), ...extraPages.flat()], [data, extraPages]);
  const effectiveNextCursor =
    manualCursor !== undefined ? manualCursor : (data?.nextCursor ?? null);
  const hasMore = effectiveNextCursor !== null;

  const recipeOrNull = recipe ?? null;
  const counts = useMemo(() => {
    let activas = 0;
    let conPendientes = 0;
    for (const a of items) {
      if (isUnitActivated(a, recipeOrNull)) activas += 1;
      if ((a.pendingObservations ?? 0) > 0) conPendientes += 1;
    }
    return {
      total: items.length,
      activas,
      sinActivar: items.length - activas,
      conPendientes,
      alDia: items.length - conPendientes,
    };
  }, [items, recipeOrNull]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((a) => {
      const activada = isUnitActivated(a, recipeOrNull);
      if (activacion === "1" && !activada) return false;
      if (activacion === "0" && activada) return false;
      const pend = (a.pendingObservations ?? 0) > 0;
      if (gestion === "yes" && !pend) return false;
      if (gestion === "no" && pend) return false;
      if (!ql) return true;
      const hay = `${a.unit ?? ""} ${a.address} ${a.ownerLabel ?? ""}`.toLowerCase();
      return hay.includes(ql);
    });
  }, [items, recipeOrNull, activacion, gestion, q]);

  async function loadMore() {
    if (!effectiveNextCursor || !id) return;
    setLoadingMore(true);
    try {
      const page = await carteraDashboardApi.assets(id, {
        limit: PAGE_LIMIT,
        cursor: effectiveNextCursor,
      });
      setExtraPages((prev) => [...prev, page.items]);
      setManualCursor(page.nextCursor);
    } finally {
      setLoadingMore(false);
    }
  }

  // F9 (corte 8) — la columna «Entrega» sólo cuando la receta la emite
  // (contraparte externa: postventa). Facility no la ve.
  const conEntrega = items.some((a) => a.entrega !== undefined);
  const nColumnas = conEntrega ? 6 : 5;

  const columnas = (
    <tr>
      <th scope="col">{assetLabel}</th>
      <th scope="col">Activación</th>
      <th scope="col">Pendientes de gestión</th>
      {conEntrega && <th scope="col">Entrega</th>}
      <th scope="col" className="col-num">
        Observaciones
      </th>
      {/* The trailing column carries the activation trigger and the
          chevron. An empty <th> leaves a screen reader announcing a
          blank column header on every row it reads. */}
      <th scope="col">
        <span className="sr-only">Acciones</span>
      </th>
    </tr>
  );

  const noEncontrado = (
    <div className="page-pad">
      <h1 className="page-header__title">{groupLabel} no encontrado</h1>
      <p className="page-header__sub">
        El id <code>{id}</code> no corresponde a ningún {groupLabel.toLowerCase()} de tu
        organización.
      </p>
      <Link to="/proyectos" className="btn btn--secondary">
        <Icon.Arrow s={14} /> Volver a {groupPlural.toLowerCase()}
      </Link>
    </div>
  );

  if (view.kind === "error") {
    if (view.error instanceof ApiError && view.error.status === 404) return noEncontrado;
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__lead">
            <div className="page-header__eyebrow">
              <Link to="/proyectos">{groupPlural}</Link>
            </div>
            <h1 className="page-header__title">{groupLabel}</h1>
          </div>
        </div>
        <div className="panel">
          {view.offline ? (
            <PageError
              title="Sin conexión"
              description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
              onRetry={view.retry}
            />
          ) : (
            <PageError
              title={`No pudimos cargar el ${groupLabel.toLowerCase()}`}
              description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
              onRetry={view.retry}
            />
          )}
        </div>
      </div>
    );
  }
  if (view.kind === "disabled" || view.kind === "empty") return noEncontrado;
  if (view.kind === "loading") {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__lead">
            <div className="page-header__eyebrow">
              <Link to="/proyectos">{groupPlural}</Link> /
            </div>
            <div className="page-header__title">
              <Skel width={380} height={26} />
            </div>
            <div className="page-header__sub">
              <Skel width={240} height={12} />
            </div>
          </div>
        </div>
        <PageSkeleton label={`Cargando ${groupLabel.toLowerCase()}…`} visible={view.visible}>
          <div className="kpi-grid" style={{ marginBottom: 20 }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="kpi-tile kpi-tile--dense skel-stack">
                <Skel width="45%" height={9} />
                <Skel width="32%" height={22} />
                <Skel width="60%" height={9} />
              </div>
            ))}
          </div>
          <div className="panel">
            <div className="panel__head">
              <div className="panel__title">{assetPlural}</div>
            </div>
            <div className="filters">
              <Skel width={260} height={30} />
              <Skel width={220} height={30} />
              <Skel width={240} height={30} />
            </div>
            <div className="panel__body--flush">
              <table className="dtable">
                <thead>{columnas}</thead>
                <tbody>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <tr key={i}>
                      <td>
                        <div className="skel-stack">
                          <Skel width="55%" height={12} />
                          <Skel width="35%" height={9} />
                        </div>
                      </td>
                      <td>
                        <Skel width={72} height={20} pill />
                      </td>
                      <td>
                        <Skel width={56} height={20} pill />
                      </td>
                      <td className="col-num">
                        <Skel width={72} height={10} />
                      </td>
                      <td>
                        <Skel width={56} height={10} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </PageSkeleton>
        {view.slow && <SlowNotice onRetry={() => void detailQuery.refetch()} />}
      </div>
    );
  }

  const { group } = view.data;
  // Los rollups del proyecto viajan en `group` con la misma forma que la
  // card de /proyectos (api-types cartera-dashboard). Opcionales porque
  // una vertical sin ciclo de observaciones no los manda.
  const total = group.assetCount;
  const activas = group.activeAssetCount ?? 0;
  const pendingTriage = group.pendingTriage ?? 0;
  const unassigned = group.unassignedObservations ?? 0;
  const pendientes = pendientesDeGestion(group);
  const withAssignee = group.withAssignee ?? 0;
  const awaiting = group.awaitingConformity ?? 0;
  const tieneObservaciones = (recipe?.observationStatuses.length ?? 0) > 0;
  const unidadesLabel = `${filtered.length} ${(filtered.length === 1 ? assetLabel : assetPlural).toLowerCase()}`;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__lead">
          <div className="page-header__eyebrow">
            <Link to="/proyectos">{groupPlural}</Link> / {group.name}
          </div>
          <h1 className="page-header__title">{group.name}</h1>
          <p className="page-header__sub">{group.subline ?? "—"}</p>
        </div>
        <div className="page-header__actions">
          {tieneObservaciones && (
            <Link to={`/proyectos/${id}/observaciones`} className="btn btn--secondary btn--sm">
              Ver observaciones
            </Link>
          )}
          <Link to={`/proyectos/${id}/archivo`} className="btn btn--ghost btn--sm">
            Ver archivo
          </Link>
          {canUsePortfolioAnalysis(sessionRol, recipe) && (
            <Link to={`/proyectos/${id}/analisis`} className="btn btn--secondary btn--sm">
              <Icon.Sparkles s={14} /> Análisis con IA
            </Link>
          )}
        </div>
      </div>

      <RefreshNotices
        refreshFailed={view.refreshFailed}
        revalidatingHint={view.revalidatingHint}
        onRetry={() => void detailQuery.refetch()}
      />

      <div
        className={kpiGridClass(cierre?.enabled === true, encuestas?.enabled === true)}
        style={{ marginBottom: 20 }}
      >
        <KpiTile
          dense
          label={`${assetPlural} activas`}
          value={activas}
          unit={`de ${total}`}
          hint={`${total - activas} sin activar`}
        />
        {/* El único tile que es enlace: lo que espera al jefe abre la
            lista ya filtrada. Sin pendientes no hay a dónde ir. */}
        <KpiTile
          dense
          label="Pendientes de gestión"
          value={pendientes}
          hint={`${pendingTriage} por aceptar · ${unassigned} por asignar`}
          review={pendientes > 0}
          {...(tieneObservaciones ? { to: `/proyectos/${id}/observaciones?f=pendientes` } : {})}
        />
        <KpiTile
          dense
          label="Con contratista"
          value={withAssignee}
          hint="Asignadas, en reparación"
        />
        <KpiTile
          dense
          label="Listas para revisar"
          value={awaiting}
          hint="El contratista terminó, falta el propietario"
        />
        <CierreKpis summary={cierre} archivoHref={`/proyectos/${id}/archivo`} />
        <EncuestaKpi summary={encuestas} encuestasHref={`/proyectos/${id}/encuestas`} />
      </div>

      <PropietariosSinContacto
        summary={cierre}
        observacionHref={(findingId) => `/proyectos/${id}/observaciones/${findingId}`}
      />

      <div className="panel">
        <div className="panel__head">
          <div className="panel__title">{assetPlural}</div>
          <div className="panel__sub mono" aria-live="polite">
            {unidadesLabel}
          </div>
        </div>
        <div className="filters">
          <div className="filter-search">
            <Icon.Search />
            <input
              placeholder={`Buscar ${assetLabel.toLowerCase()} o propietario…`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <SegRadio
            label="Activación"
            value={activacion}
            onChange={setActivacion}
            options={[
              { value: "all", label: "Todas", count: counts.total },
              { value: "1", label: "Activas", count: counts.activas },
              { value: "0", label: "Sin activar", count: counts.sinActivar },
            ]}
          />
          <SegRadio
            label="Gestión"
            value={gestion}
            onChange={setGestion}
            options={[
              { value: "all", label: "Todas", count: counts.total },
              { value: "yes", label: "Con pendientes", count: counts.conPendientes },
              { value: "no", label: "Al día", count: counts.alDia },
            ]}
          />
        </div>
        <div className="panel__body--flush">
          {items.length === 0 ? (
            <EmptyState
              title={`Este ${groupLabel.toLowerCase()} aún no tiene ${assetPlural.toLowerCase()}`}
            />
          ) : (
            <table className="dtable">
              <caption className="sr-only">Listado de {assetPlural.toLowerCase()}</caption>
              <thead>{columnas}</thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={nColumnas}>
                      <EmptyState
                        title={`Ninguna ${assetLabel.toLowerCase()} coincide con los filtros`}
                        description="Prueba con otra combinación o limpia la búsqueda."
                      />
                    </td>
                  </tr>
                ) : (
                  filtered.map((a) => (
                    <UnidadRow key={a.id} a={a} recipe={recipeOrNull} assetLabel={assetLabel} />
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
        {hasMore && (
          <div className="pager">
            <span className="pager__meta" aria-live="polite">
              Mostrando {items.length} {assetPlural.toLowerCase()}
            </span>
            <button
              type="button"
              className="pager__btn"
              onClick={() => void loadMore()}
              disabled={loadingMore}
            >
              {loadingMore ? "Cargando…" : "Cargar más"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Un seg-toggle que es un radiogroup de verdad: una sola opción activa,
// tabindex itinerante (sólo la activa entra en el orden de tabulación,
// las flechas mueven y seleccionan a la vez) — el patrón APG de radio
// group, que es lo que un filtro exclusivo ES. El conteo va en el nombre
// accesible después del texto visible (WCAG 2.5.3).
type SegOption<T extends string> = { value: T; label: string; count: number };

function SegRadio<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly SegOption<T>[];
  onChange: (v: T) => void;
}) {
  const labelId = useId();

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const idx = options.findIndex((o) => o.value === value);
    let next: number | null = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (idx + 1) % options.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp")
      next = (idx - 1 + options.length) % options.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = options.length - 1;
    if (next === null) return;
    e.preventDefault();
    const option = options[next];
    if (!option) return;
    onChange(option.value);
    const radios = e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    radios[next]?.focus();
  }

  return (
    <div className="filters__group">
      <span className="filters__label" id={labelId}>
        {label}
      </span>
      <div
        className="seg-toggle"
        role="radiogroup"
        aria-labelledby={labelId}
        onKeyDown={handleKeyDown}
      >
        {options.map((o) => {
          const checked = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={checked}
              aria-label={`${o.label}, ${o.count}`}
              tabIndex={checked ? 0 : -1}
              className={`seg-toggle__btn ${checked ? "seg-toggle__btn--active" : ""}`}
              onClick={() => onChange(o.value)}
            >
              {o.label}
              <span className="seg-toggle__n" aria-hidden="true">
                {o.count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function UnidadRow({
  a,
  recipe,
  assetLabel,
}: {
  a: CarteraDashboardAssetItem;
  recipe: RecipeMeResponse | null;
  assetLabel: string;
}) {
  const session = useSession();
  const navigate = useNavigate();
  const [activateOpen, setActivateOpen] = useState(false);
  const [entregaOpen, setEntregaOpen] = useState(false);
  const label = unitTitle(a);

  // Both the predicate and the pill label come out of the recipe, so a
  // vertical that names its statuses differently gets the right word
  // for free. A unit that is not activated reads «Sin activar» whatever
  // its declared status is — the recipe exposes no way to tell
  // «no activada» from «fuera de garantía» on the wire, and nothing
  // writes the latter today (recipe comment on
  // observationsRequireAssetStatusIn).
  const isActivated = isUnitActivated(a, recipe);
  const statusLabel =
    recipe?.assetStatuses.find((s) => s.key === a.assetStatus)?.label ?? "Sin activar";

  // Whether this vertical activates units at all: the recipe declares a
  // live (non-retired) asset-scoped member role whose observation actor
  // is the asset's own party — the same primitive
  // `resolveAssetPartyMemberRole` resolves server-side. A retired role
  // must not count, or a legacy declaration would keep the button alive
  // after the vertical moved on.
  const hasOwnerInvitationRole = (recipe?.memberRoles ?? []).some(
    (r) =>
      r.retired !== true && r.observationStatusActor === "asset_party" && r.scopeType === "asset"
  );
  // And whether THIS caller may create invitations at all. Deliberately
  // NOT also gated on the caller's own scope being org-wide: ADR-0124
  // Decisión 10 gave jefe_proyecto a project scope, so a jefe inside
  // their own project can activate. A jefe pointed at someone else's
  // project still gets a server 403, which the drawer maps to the scope
  // branch — the server, not this predicate, is the boundary.
  const canActivate =
    hasOwnerInvitationRole &&
    (session?.user.platformAdmin === true ||
      (session !== null && isInvitationCreateRole(session.organization.role)));

  // An activated unit still offers a re-invite: the only authority on
  // "this owner already has an account" is the 409 the server returns
  // from the locked row, and a supervisor whose invitee mistyped their
  // address needs a second link. Activar is the row's primary action
  // (secondary button); the re-invite is the quiet one (ghost).
  const actionText = isActivated ? "Reenviar invitación" : "Activar unidad";
  const actionClass = isActivated ? "btn btn--ghost btn--sm" : "btn btn--secondary btn--sm";

  const pendientes = a.pendingObservations ?? 0;

  // F9 (corte 8) — la entrega programada. La fija el carril que asigna
  // (jefe en sus proyectos, gerente en la org) o `admin` (personal
  // interno): el mismo predicado que valida el servidor, que sigue siendo
  // la frontera. Una unidad ya entregada no ofrece la acción (el servidor
  // responde 409 `DELIVERY_ALREADY_HAPPENED`).
  const conEntrega = a.entrega !== undefined;
  const sessionRole = session?.organization.role ?? null;
  const yaEntregada = deliveryAlreadyHappened(a.entrega ?? null, new Date());
  const canScheduleDelivery =
    conEntrega &&
    !yaEntregada &&
    sessionRole !== null &&
    (sessionRole === "admin" ||
      (recipe?.memberRoles ?? []).some(
        (r) => r.key === sessionRole && r.observationStatusActor === "assigning_party"
      ));
  const entregaText = a.entrega ? "Cambiar entrega" : "Programar entrega";

  // Las observaciones de esta unidad viven en su propia ruta (panel v2):
  // el filtro viaja por la URL para que el jefe pueda compartir «las de
  // la 402» sin explicar el camino. La fila entera navega — no tiene
  // casilla de selección, así que no hay conflicto de gesto (decisión 10
  // del 2026-09-29) — y el chevron sigue ahí para el teclado y el lector.
  const observacionesHref = `/proyectos/${a.assetGroupId}/observaciones?unidad=${a.id}`;

  function handleRowClick(e: React.MouseEvent<HTMLTableRowElement>) {
    if ((e.target as HTMLElement).closest('a, button, [role="button"]')) return;
    navigate(observacionesHref);
  }

  return (
    <>
      <tr className="row-link" onClick={handleRowClick}>
        <td>
          <div className="cell-with-sub">
            <span className="unit-cell__n">{label}</span>
            {a.ownerLabel ? (
              <span className="unit-cell__owner">{a.ownerLabel}</span>
            ) : (
              <span className="unit-cell__owner unit-cell__owner--none">Sin propietario</span>
            )}
          </div>
        </td>
        <td>
          <span className={`st-pill ${isActivated ? "st-pill--ok" : "st-pill--idle"}`}>
            {isActivated ? statusLabel : "Sin activar"}
          </span>
        </td>
        <td>
          {pendientes > 0 ? (
            <span className="st-pill st-pill--review">
              {pendientes} {pendientes === 1 ? "pendiente" : "pendientes"}
            </span>
          ) : (
            <span className="cell-meta">Al día</span>
          )}
        </td>
        {conEntrega && (
          <td>
            {a.entrega ? (
              <span className="cell-meta">{capitalizar(entregaLabel(a.entrega, new Date()))}</span>
            ) : (
              <span className="cell-meta">Sin fecha</span>
            )}
          </td>
        )}
        <td className="col-num">
          {/* Regla de datos: una unidad sin activar no tiene observaciones
              (sólo el propietario reporta), así que cero y «no aplica»
              se ven igual: «—». */}
          {a.openObservations > 0 ? (
            <>
              {a.openObservations} <span className="cell-sub">abiertas</span>
            </>
          ) : (
            <span className="cell-meta">—</span>
          )}
        </td>
        {/* The trigger shares the trailing actions cell with the chevron,
            DOM order button-then-chevron. The aria-label appends the unit
            so the accessible name is unique per row — and it STARTS with
            the visible text, which is what WCAG 2.5.3 (Label in Name)
            requires for voice control. */}
        <td className="cell-actions">
          {canScheduleDelivery && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              aria-label={`${entregaText} ${label}`}
              onClick={() => setEntregaOpen(true)}
            >
              {entregaText}
            </button>
          )}
          {canActivate && (
            <button
              type="button"
              className={actionClass}
              aria-label={`${actionText} ${label}`}
              onClick={() => setActivateOpen(true)}
            >
              {actionText}
            </button>
          )}
          <Link
            to={observacionesHref}
            className="row-chevron"
            aria-label={`Ver observaciones de ${label}`}
          >
            <Icon.ChevronRight />
          </Link>
        </td>
      </tr>
      {/* Rendered as a sibling of the <tr>, never inside it — the drawer
          itself portals to document.body, but keeping the JSX out of the
          row's cells keeps the table's own markup valid regardless. */}
      {activateOpen && (
        <ActivateUnitDrawer
          asset={a}
          assetLabel={assetLabel}
          alreadyActivated={isActivated}
          onClose={() => setActivateOpen(false)}
        />
      )}
      {entregaOpen && (
        <EntregaDrawer asset={a} assetLabel={assetLabel} onClose={() => setEntregaOpen(false)} />
      )}
    </>
  );
}

function capitalizar(s: string): string {
  return s.charAt(0).toLocaleUpperCase("es-CL") + s.slice(1);
}
